import type { PrescriptionLine } from '~/models/PrescriptionLine'
import { PullService } from '../servicePull'
import type { PrescriptionLineTotalAmountInfoModel } from '../../services/functionalities/prescriptionLineTotalAmountInfoModel'
import { getDocs, where, query } from 'firebase/firestore'
import type { InsuranceCompany, MedicineArticle } from '~/models'
import { CodeType } from '~/models/PrescriptionLineContent'
import type { Controller } from '~/features/controller'
import type { ControllerState } from '~/features/controllerState'
import { ControllerStateArray } from '~/features/controllerStateArray'

export class PrescriptionLineTotalAmountInfoService extends PullService<
  PrescriptionLineTotalAmountInfoModel,
  PrescriptionLineTotalAmountInfoModel[]
> {
  private firebase

  override createControllerState(
    controller: Controller,
    sideEffect?: () => void,
  ): ControllerState<
    PrescriptionLineTotalAmountInfoModel,
    PrescriptionLineTotalAmountInfoModel[]
  > {
    return new ControllerStateArray(controller, sideEffect)
  }

  constructor() {
    super()
    this.firebase = useFirebase()
  }

  public async getPrescriptionLineById(
    prescriptionLineId: string,
  ): Promise<PrescriptionLine | undefined> {
    return await this.firebase.getDocument<PrescriptionLine>(
      `/PrescriptionLine/${prescriptionLineId}`,
    )
  }

  public async getMedicineArticleByCodeType(code: number, codeType: CodeType) {
    let articleQuery
    if (codeType === CodeType.KNMP) {
      articleQuery = query(
        this.firebase.collection('MedicineArticles'),
        where('ArticleNumber', '==', code),
      )
    }

    if (!articleQuery) {
      console.warn('Invalid codeType: ' + codeType)
      return null
    }

    const { docs } = await getDocs(articleQuery)
    if (docs.length > 0) {
      return docs[0].data() as MedicineArticle
    }
    return null
  }

  public async getInsuranceCompanyByUzovi(
    uzovi: string,
  ): Promise<InsuranceCompany | undefined> {
    const queryInsuranceCompany = query(
      this.firebase.collection('InsuranceCompanies'),
      where('Uzovi', '==', uzovi),
    )

    const { docs } = await getDocs(queryInsuranceCompany)

    if (docs.length === 0) {
      return undefined
    }

    return docs[0].data() as InsuranceCompany
  }

  @PullService.CallableFunction<PrescriptionLineTotalAmountInfoModel[]>()
  public async getPrescriptionLineTotalAmountInfo(
    prescriptionLine: PrescriptionLine,
  ): Promise<PrescriptionLineTotalAmountInfoModel[]> {
    const prescriptionLineMedication = prescriptionLine.Content?.Medication
    if (!prescriptionLineMedication) {
      console.info(
        'Prescription line does not have a valid medication prescription line',
      )
      return []
    }

    const medicineArticle = await this.getMedicineArticleByCodeType(
      Number(prescriptionLineMedication.Code),
      prescriptionLineMedication.CodeType,
    )

    if (!medicineArticle) {
      console.info(
        `Medicine article, ${prescriptionLineMedication.CodeType}: ${prescriptionLineMedication.Code} not found`,
      )
      return []
    }

    const uzovi = prescriptionLine.Content?.Patient.InsurerCode
    if (!uzovi) {
      console.info('Prescription line does not have a valid uzovi')
      return []
    }
    const insuranceCompany = await this.getInsuranceCompanyByUzovi(uzovi)

    if (!insuranceCompany) {
      console.info('Insurance company not found with uzovi' + uzovi)
      return []
    }

    return [
      {
        id: prescriptionLine.id,
        medicineArticle,
        insuranceCompany,
      },
    ]
  }
}
